import React, { CSSProperties } from 'react';
import { useHistory } from 'react-router-dom';
import Colors from 'modules/Colors';
import Icon from 'elements/Icon'; // Import the Icon element
import useScreen from 'Hooks/useScreen';
import { OLD_SITE_URL } from 'modules/Utils';

interface FooterProps {
    className?: string;
}

const Footer = ({ className }: FooterProps) => {
    const history = useHistory();
    const { mStyle } = useScreen();


    const handleNavigation = (path: string) => {
        history.push(path);
    };

    const handleExternalNavigation = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const sitemapLinks = [
        { text: 'Maestro', path: '/', isExternal: false },
        { text: 'About Us', path: `${OLD_SITE_URL}/about`, isExternal: true },
        { text: 'Pricing', path: `/pricing`, isExternal: true },
        { text: 'Partnerships', path: `${OLD_SITE_URL}/partnerships`, isExternal: true },

    ];

    const platformLinks = [
        { text: 'Meet Maestro, your AI agency', path: '/', isExternal: false },
        { text: 'Website Creation', path: `${OLD_SITE_URL}/solutions/website-creation`, isExternal: true },
        { text: 'Automated Ads', path: `${OLD_SITE_URL}/solutions/automated-marketing`, isExternal: true },
        { text: 'Cross-platform Analytics', path: `${OLD_SITE_URL}/what-is-possible/identify-and-understand-your-audience`, isExternal: true },
    ];

    const socialLinks = [
        { name: 'Instagram', url: 'https://www.instagram.com/symphony.os/', icon: 'instagramFlat' },
        { name: 'TikTok', url: 'https://www.tiktok.com/@symphony.os', icon: 'tiktokFlat' },
        { name: 'Twitter', url: 'https://twitter.com/symphonyos_', icon: 'xFlat' },
        { name: 'YouTube', url: 'https://www.youtube.com/@Symphony-OS', icon: 'youtubeFlat' },
    ];

    const legalLinks = [
        { text: 'Terms of Service', path: `${OLD_SITE_URL}/terms-of-service` },
        { text: 'Privacy Policy', path: `${OLD_SITE_URL}/privacy-policy` },
        { text: 'Cancellation & Refund Policy', path: `${OLD_SITE_URL}/cancellation-refund-policy` },
    ];

    return (
        <footer className={className} style={sty.footerContainer}>
            <div className="footerContent" style={sty.footerContent}>
                <div className="footerSitemapSection" style={mStyle(sty, "sitemapSection")}>
                    <h3 className="footerSectionTitle" style={sty.sectionTitle}>Sitemap</h3>
                    <ul className="footerLinkList" style={sty.linkList}>
                        {sitemapLinks.map((link, index) => (
                            <li 
                                key={index}
                                className="footerLinkItem" 
                                style={sty.linkItem} 
                                onClick={() => link.isExternal ? handleExternalNavigation(link.path) : handleNavigation(link.path)}
                            >
                                {link.text}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="footerPlatformSection" style={sty.platformSection}>
                    <h3 className="footerSectionTitle" style={sty.sectionTitle}>Platform</h3>
                    <ul className="footerLinkList" style={sty.linkList}>
                        {platformLinks.map((link, index) => (
                            <li 
                                key={index}
                                className="footerLinkItem" 
                                style={sty.linkItem} 
                                onClick={() => link.isExternal ? handleExternalNavigation(link.path) : handleNavigation(link.path)}
                            >
                                {link.text}
                            </li>
                        ))}
                    </ul>
                </div>
                <div className="footerSocialSection" style={sty.socialSection}>
                    <h3 className="footerSectionTitle" style={sty.sectionTitle}>Follow Us</h3>
                    <div className="footerSocialIconsContainer" style={sty.socialIconsContainer}>
                        {socialLinks.map((social, index) => (
                            <a 
                                key={index}
                                href={social.url} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="footerSocialLink" 
                                style={sty.socialLink}
                            >
                                <Icon className="footerSocialIcon" icon={social.icon} size={24} color={Colors.white} />
                            </a>
                        ))}
                    </div>
                </div>
            </div>
            <div className="footerBottom" style={sty.footerBottom}>
                <p className="footerCopyrightText" style={sty.copyrightText}>&copy; {new Date().getFullYear()} SymphonyOS. All rights reserved.</p>
                <div className="footerLegalLinksContainer" style={sty.legalLinksContainer}>
                    {legalLinks.map((link, index) => (
                        <React.Fragment key={index}>
                            <a 
                                className="footerLegalLinkItem" 
                                style={sty.legalLinkItem} 
                                href={link.path} 
                                target="_blank" 
                                rel="noopener noreferrer"
                            >
                                {link.text}
                            </a>
                            {index < legalLinks.length - 1 && (
                                <span className="footerLegalLinkSeparator" style={sty.legalLinkSeparator}>|</span>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </footer>
    );
};

const sty: Record<string, CSSProperties> = {
    footerContainer: {
        backgroundColor: Colors.purpleDark,
        color: Colors.white,
        padding: '40px 20px',
        borderTop: `1px solid ${Colors.greyDark}`,
    },
    footerContent: {
        display: 'flex',
        justifyContent: 'space-around',
        flexWrap: 'wrap',
        maxWidth: '1200px',
        margin: '0 auto',
        paddingBottom: '20px',
    },
    sitemapSection: {
        margin: '20px',
        minWidth: '200px',
    },
    sitemapSection_mobile: {
        margin: '0px'
    },
    platformSection: {
        margin: '20px',
        minWidth: '200px',
    },
    socialSection: {
        margin: '20px',
        minWidth: '200px',
    },
    sectionTitle: {
        fontSize: '18px',
        marginBottom: '15px',
        color: Colors.white,
        fontWeight: 'bold',
    },
    linkList: {
        listStyle: 'none',
        padding: 0,
    },
    linkItem: {
        marginBottom: '10px',
        cursor: 'pointer',
        fontSize: '14px',
        color: Colors.greyLight,
        transition: 'color 0.2s ease',
    },
    socialIconsContainer: {
        display: 'flex',
        gap: '15px',
        alignItems: 'center', // Align icons nicely
    },
    socialLink: {
        color: Colors.white,
        textDecoration: 'none',
        display: 'inline-flex', // Use inline-flex for better alignment of icon within the link
        alignItems: 'center',
    },
    footerBottom: {
        textAlign: 'center',
        paddingTop: '20px',
        borderTop: `1px solid ${Colors.greyDark}`,
        fontSize: '12px',
        color: Colors.greyLight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '10px', // Space between copyright and legal links
    },
    copyrightText: {
        margin: 0, // Reset default margin
    },
    legalLinksContainer: {
        display: 'flex',
        gap: '5px', // Space between separator and link
        alignItems: 'center',
        flexWrap: 'wrap', // Allow links to wrap on smaller screens
        justifyContent: 'center',
    },
    legalLinkItem: {
        color: Colors.greyLight,
        textDecoration: 'none',
        fontSize: '12px',
        transition: 'color 0.2s ease',
    },
    legalLinkSeparator: {
        color: Colors.greyLight,
        fontSize: '12px',
        margin: '0 5px', // Add some space around the separator
    },
};

export default Footer;
